import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import type { CatalogItemDto, CatalogShop, HomeStageOfferDto } from "@townsy/contracts";
import { WORLD_MANIFEST } from "@townsy/world";
import { stageLabel, type DecorScope } from "../../homes/homeLogic";
import { ItemPurchaseDialog, UpgradeDialog } from "./HomeReviewDialogs";
import { HomeShopView, SHOP_INFO } from "./HomeShopView";
import { itemCheckout, upgradeCheckout } from "../../commerce/checkoutTargets";
import { useTownStore } from "../../state/store";
import { useDollarGate } from "../checkout/useDollarGate";
import { MyHomeManager } from "./MyHomeManager";
import { useHomeActions, useHomeData } from "./useHomeData";

/**
 * Townsy Home Builders, Townsy Furnishings and "My home" on the accessible `/directory` page and in
 * the no-3D fallback (docs/01 section 7): the same upgrades, purchases (same review dialogs and
 * Idempotency-Key handling), look editor and placement as in the 3D town, so a home can be
 * upgraded, furnished and arranged without WebGL. Here the uncommitted placement preview is the
 * text line under the choices.
 */
export function DirectoryHomes() {
  const data = useHomeData(true);
  const actions = useHomeActions(null);
  const dollar = useDollarGate();
  const openCheckout = useTownStore((s) => s.openCheckout);
  const [buying, setBuying] = useState<CatalogItemDto | null>(null);
  const [upgrading, setUpgrading] = useState<HomeStageOfferDto | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [tab, setTab] = useState<DecorScope>("yard");
  // Links such as /directory#my-home (account page) land on their section once it has rendered.
  const hasHome = Boolean(data.home);
  const catalogReady = data.catalogState === "ready";
  const scrolled = useRef(false);
  useEffect(() => {
    if (scrolled.current || typeof window === "undefined" || !catalogReady) return;
    const hash = window.location.hash.slice(1);
    if (!hash || (hash === "my-home" && !hasHome)) return;
    const el = document.getElementById(hash);
    if (!el) return;
    scrolled.current = true;
    el.scrollIntoView({ block: "start" });
  }, [hasHome, catalogReady]);
  const realEstateLink = (
    <Link className="btn btn-sm" to="/directory#real-estate">
      Buy a plot
    </Link>
  );
  const shopLink = (shop: CatalogShop) => (
    <a className="btn btn-sm" href={`#${shop === "home_builders" ? "home-builders" : "furnishings"}`}>
      {shop === "home_builders" ? "Buy at Townsy Home Builders" : "Buy at Townsy Furnishings"}
    </a>
  );
  const openMyHome = (next: DecorScope) => {
    setTab(next);
    if (typeof document !== "undefined") document.getElementById("my-home")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const shopSection = (shop: CatalogShop, anchor: string) => (
    <section className="stack directory-shop" aria-labelledby={`${anchor}-title`} id={anchor} data-testid={`directory-${anchor}`}>
      <h2 id={`${anchor}-title`}>
        {SHOP_INFO[shop].name} · {SHOP_INFO[shop].address}
      </h2>
      <HomeShopView
        shop={shop}
        data={data}
        onBuy={setBuying}
        onUpgrade={setUpgrading}
        dollar={dollar}
        onBuyUsd={(item) => openCheckout(itemCheckout(item))}
        onUpgradeUsd={(next) => data.home && openCheckout(upgradeCheckout(data.home, next))}
        onSaveLook={async (look) => (data.home ? actions.saveLook(data.home, look) : { ok: false, code: "HOME_REQUIRED", message: "Buy a plot first.", conflict: false })}
        onOpenMyHome={data.home ? openMyHome : null}
        realEstateAction={realEstateLink}
      />
    </section>
  );

  return (
    <div className="stack" data-testid="directory-homes">
      {notice ? (
        <div className="notice notice-success" role="status" data-testid="directory-homes-notice">
          {notice}
        </div>
      ) : null}
      {shopSection("home_builders", "home-builders")}
      {shopSection("furnishings", "furnishings")}
      {data.home ? (
        <section className="stack" aria-labelledby="my-home-title" id="my-home" data-testid="directory-my-home">
          <h2 id="my-home-title">My home</h2>
          <MyHomeManager
            home={data.home}
            inventory={data.inventory}
            catalogItems={data.catalog?.items ?? null}
            manifest={WORLD_MANIFEST}
            tab={tab}
            onTab={setTab}
            actions={actions}
            sink={null}
            epoch="directory"
            town={null}
            shopAction={shopLink}
          />
        </section>
      ) : null}
      {buying && data.wallets ? (
        <ItemPurchaseDialog
          item={buying}
          coinsAvailable={data.wallets.coins.available}
          onClose={() => setBuying(null)}
          onStale={actions.refreshStale}
          onPurchased={(res) => {
            const item = buying;
            setBuying(null);
            actions.afterItemPurchase(res);
            setNotice(`${item.name} is in your storage. Place it under "My home" below.`);
          }}
        />
      ) : null}
      {upgrading && data.home && data.wallets ? (
        <UpgradeDialog
          home={data.home}
          next={upgrading}
          coinsAvailable={data.wallets.coins.available}
          onClose={() => setUpgrading(null)}
          onStale={actions.refreshStale}
          onUpgraded={(res) => {
            setUpgrading(null);
            actions.afterUpgrade(res);
            setNotice(`Your home is now a ${stageLabel(res.stage).toLowerCase()}.`);
          }}
        />
      ) : null}
    </div>
  );
}
